import { DiaryFormElement } from "./diary-form-element.model";
import { DiaryFormElementType } from "../../constants/diary-form-element-type.enum";
import { DiaryFormOption } from "./diary-form-option.model";
import { DiaryFormFrequencyType } from "../../constants/diary-form-frequency-type.enum";
import { DiaryType } from "../../constants/diary-form-type.enum";
import { DiaryFormRule } from "./diary-form-rule.model";
import { DiaryFormNotificationTranslation } from "./diary-form-notification-translation.model";

export class DiaryForm {
  id: string;
  name: string;
  elements: DiaryFormElement[] = [];
  enabled: boolean;
  deleted: boolean;
  frequencyType: DiaryFormFrequencyType;
  frequencyInterval: number | null = null;
  rules: DiaryFormRule[] = [];
  notificationTranslations: DiaryFormNotificationTranslation[] = [];
  diaryType: DiaryType = DiaryType.Periodic;
  hasEntries: boolean = false;
  elementIdsWithEntries: string[] = [];
  defaultReminderTimeMinutes: number;
  defaultIntraDayReminderTimes: number[] = [];
  overrideCompletedTitle: boolean;
  overrideTitle: boolean;
  mandatoryNotification: boolean;
  lastEntryDate?: Date;

  constructor(init?: Partial<DiaryForm>) {
    Object.assign(this, init);

    if (init) {
      if (init && init.notificationTranslations && init.notificationTranslations.length > 0) {
        this.notificationTranslations = init.notificationTranslations.map(t => new DiaryFormNotificationTranslation(t));
      }

      // Elements
      if (init.elements && init.elements.length > 0) {
        this.elements = init.elements.filter(e => !e.deleted).map(e => new DiaryFormElement(e));
      }

      // Rules
      if (init.rules && init.rules.length > 0) {
        this.rules = init.rules.map(c => new DiaryFormRule(c));
      }

      if (init.lastEntryDate)
        this.lastEntryDate = new Date(init.lastEntryDate);
    }
  }

  addSingleLineTextElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.SingleLineText,
      label: 'Single line question',
      placeholder: 'Enter a value...'
    }));
  }

  addMultiLineTextElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.MultiLineText,
      label: 'Multi line question',
      placeholder: 'Enter a value...'
    }));
  }

  addDropdownElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Dropdown,
      label: 'Dropdown question',
      placeholder: 'Select an option...',
      options: [
        new DiaryFormOption({ text: 'Option 1', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 2', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 3', id: crypto.randomUUID().replace(/-/g, '')}),
      ]
    }));
  }

  addRadioElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Radio,
      label: 'Single choice (radio) question',
      options: [
        new DiaryFormOption({ text: 'Option 1', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 2', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 3', id: crypto.randomUUID().replace(/-/g, '')})
      ]
    }));
  }

  addCheckboxElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Checkbox,
      label: 'Multiple choice (checkbox) question',
      options: [
        new DiaryFormOption({ text: 'Option 1', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 2', id: crypto.randomUUID().replace(/-/g, '')}),
        new DiaryFormOption({ text: 'Option 3', id: crypto.randomUUID().replace(/-/g, '')}),
      ]
    }));
  }

  addNumericElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Numeric,
      label: 'Numeric question',
      placeholder: 'Enter a value...'
    }));
  }

  addDateElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Date,
      label: 'Date question',
    }));
  }

  addTimeElement() {
    this.elements.push(new DiaryFormElement({
      elementType: DiaryFormElementType.Time,
      label: 'Time question',
    }));
  }

  /**
   * Moves the element at the specified index up one position
   * @param index The index of the element to move up
   * @returns True if the move was successful, false otherwise
   */
  moveElementUp(index: number): boolean {
    if (index <= 0 || index >= this.elements.length) {
      return false; // Can't move up if it's the first element or invalid index
    }

    // Get the elements being swapped
    const currentElement = this.elements[index];
    const previousElement = this.elements[index - 1];

    // Swap the elements
    this.elements[index - 1] = currentElement;
    this.elements[index] = previousElement;

    // Update orderIndex to reflect the new positions
    currentElement.orderIndex = index - 1;
    previousElement.orderIndex = index;

    return true;
  }

  /**
   * Moves the element at the specified index down one position
   * @param index The index of the element to move down
   * @returns True if the move was successful, false otherwise
   */
  moveElementDown(index: number): boolean {
    // Can't move down if it's the last element, array is empty, or index is invalid
    if (index < 0 || index >= this.elements.length - 1 || this.elements.length <= 1) {
      return false;
    }

    // Get the elements being swapped
    const currentElement = this.elements[index];
    const nextElement = this.elements[index + 1];

    // Swap the elements
    this.elements[index] = nextElement;
    this.elements[index + 1] = currentElement;

    // Update orderIndex to reflect the new positions
    currentElement.orderIndex = index + 1;
    nextElement.orderIndex = index;

    return true;
  }

  /**
   * Removes an element at the specified index and updates the order indexes
   * @param index The index of the element to remove
   * @returns The removed element or undefined if index is out of bounds
   */
  removeElementAt(index: number): DiaryFormElement | undefined {
    if (index < 0 || index >= this.elements.length) {
      return undefined;
    }

    // Remove the element at the specified index
    const [removedElement] = this.elements.splice(index, 1);

    return removedElement;
  }

  removeRuleAt(index: number): DiaryFormRule | undefined {
    if (index < 0 || index >= this.rules.length) {
      return undefined;
    }

    // Remove the element at the specified index
    const [removedRule] = this.rules.splice(index, 1);

    return removedRule;
  }
}
